/**
 * The promotion applied to a subscription, as one line of the subscriber detail (TMMAPI-0124;
 * TMMRSL-0070). Pure, so it can be unit-tested. The code is shown as is; the terms follow the
 * kind; the statements left are only for the two variable kinds. There is no end date: a
 * discount ends after `statementsRemaining` statements or when someone ends it, so the code's
 * own `validUntil` is never shown here. No unknown value renders as a raw enum.
 */
import type { TFunction } from 'i18next';
import type { AppliedPromotion, OfferView } from '@/api/generated';
import { moneyIn } from '@/components/moneyIn';
import { bpToPercentText, firstCurrency, formatInstant } from '@/i18n/format';
import { offerById, offerLabel, offerPeriodLabel } from './offerLabel';

export interface PromotionContext {
  t: TFunction;
  locale: string;
  /** The open tenant's currency: the fallback for an amount-off promotion that names none. */
  currency: string;
  offers: OfferView[];
  /** The IANA zone `appliedAt` is shown in; the browser's when absent. */
  timeZone?: string;
}

/** The line split around the code, so the caller can set the code in the body weight. */
export interface PromotionSummary {
  before: string;
  code: string;
  after: string;
  /** `before + code + after`. */
  text: string;
}

/** Stands in for the code while the translation is built, so the code is never re-parsed. */
const CODE_SLOT = '';

function isVariable(kind: AppliedPromotion['kind']): boolean {
  return kind === 'PERCENT_OFF_VARIABLE' || kind === 'AMOUNT_OFF_VARIABLE';
}

function termsOf(promo: AppliedPromotion, ctx: PromotionContext, unavailable: string): string | null {
  const { t, locale } = ctx;
  if (promo.kind === 'PERCENT_OFF_VARIABLE') {
    if (promo.percentBp === null) return null;
    return t('subscribers.promotion.terms.percent', {
      percent: bpToPercentText(promo.percentBp, locale),
    });
  }
  if (promo.kind === 'AMOUNT_OFF_VARIABLE') {
    if (promo.amountMinor === null) return null;
    // The promotion's own currency wins over the tenant's (TMM-B1).
    const currency = firstCurrency(promo.currency, ctx.currency);
    const amount = moneyIn(promo.amountMinor, currency, locale, unavailable);
    if (amount === unavailable) return null;
    return t('subscribers.promotion.terms.amount', { amount });
  }
  if (promo.kind === 'OFFER') {
    if (promo.retailOfferId === null) return null;
    const offer = offerById(ctx.offers, promo.retailOfferId);
    const label = offerLabel(
      offer,
      ctx.currency,
      locale,
      offerPeriodLabel(offer?.interval, t('dashboard.perMonth'), t('dashboard.perYear')),
      unavailable,
    );
    if (label === unavailable) return null;
    return t('subscribers.promotion.terms.offer', { offer: label });
  }
  return null;
}

function appliedOn(promo: AppliedPromotion, ctx: PromotionContext, unavailable: string): string {
  try {
    return formatInstant(
      promo.appliedAt,
      ctx.locale,
      ctx.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      { dateStyle: 'medium' },
    );
  } catch {
    return unavailable;
  }
}

export function promotionSummary(promo: AppliedPromotion, ctx: PromotionContext): PromotionSummary {
  const { t } = ctx;
  const unavailable = t('subscribers.unavailable');
  const name = promo.name.trim();
  const params = {
    code: CODE_SLOT,
    // The name may follow the code when it differs, as the apply dialog's menu does.
    name:
      name !== '' && name !== promo.code ? t('subscribers.promotion.nameSuffix', { name }) : '',
    terms: termsOf(promo, ctx, unavailable) ?? t('subscribers.promotion.terms.unknown'),
    date: appliedOn(promo, ctx, unavailable),
  };
  let line: string;
  if (isVariable(promo.kind)) {
    const remaining =
      promo.statementsRemaining === null
        ? t('subscribers.promotion.untilEnded')
        : t('subscribers.promotion.remaining', { count: promo.statementsRemaining });
    line = t('subscribers.promotion.line', { ...params, remaining });
  } else {
    line = t('subscribers.promotion.lineOffer', params);
  }
  const at = line.indexOf(CODE_SLOT);
  if (at < 0) return { before: line, code: '', after: '', text: line };
  const before = line.slice(0, at);
  const after = line.slice(at + CODE_SLOT.length);
  return { before, code: promo.code, after, text: `${before}${promo.code}${after}` };
}
